/**
 * features/control/constants.ts — German UI strings + the stage colour map for the
 * Kontrollbereich Cockpit (Bereich 5). Labels ported verbatim from Stefan's prototype
 * (V4.0-inbox/cockpit-screenshots). Code stays English; user-facing text is German.
 *
 * The stage class strings are FULL literals (never `bg-stage-${n}-bg`) so the Tailwind
 * scanner picks them up — the tokens are defined in index.css (--color-stage-N-*).
 */

import type {
  KontrolleKanal,
  KontrolleSignalTyp,
  KontrolleStufe,
} from './types';

/** The 4 age-stages, in order, with their name + seeded default day threshold. */
export const STUFEN: ReadonlyArray<{ stufe: KontrolleStufe; name: string; defaultTage: number }> = [
  { stufe: 1, name: 'Beobachtung', defaultTage: 2 },
  { stufe: 2, name: 'Nachfassen', defaultTage: 5 },
  { stufe: 3, name: 'Kritisch', defaultTage: 10 },
  { stufe: 4, name: 'Eskalation', defaultTage: 14 },
];

/** Stage name by index. */
export const STUFE_NAME: Record<KontrolleStufe, string> = {
  1: 'Beobachtung',
  2: 'Nachfassen',
  3: 'Kritisch',
  4: 'Eskalation',
};

/** Seeded default day threshold by stage (the "Default: N Tage" hint on the config screen). */
export const STUFE_DEFAULT_TAGE: Record<KontrolleStufe, number> = {
  1: 2,
  2: 5,
  3: 10,
  4: 14,
};

/**
 * Per-stage Tailwind class bundle (literal strings for the scanner). `tile` = soft-badge
 * fill + border for tiles/pills; `text` = the saturated label/count colour; `dot` = the
 * status-dot fill; `bar` = the gradient-bar segment fill.
 */
export const STUFE_CLASSES: Record<
  KontrolleStufe,
  { tileBg: string; border: string; text: string; dot: string; bar: string }
> = {
  1: {
    tileBg: 'bg-stage-1-bg',
    border: 'border-stage-1-border',
    text: 'text-stage-1-text',
    dot: 'bg-stage-1-dot',
    bar: 'bg-stage-1-dot',
  },
  2: {
    tileBg: 'bg-stage-2-bg',
    border: 'border-stage-2-border',
    text: 'text-stage-2-text',
    dot: 'bg-stage-2-dot',
    bar: 'bg-stage-2-dot',
  },
  3: {
    tileBg: 'bg-stage-3-bg',
    border: 'border-stage-3-border',
    text: 'text-stage-3-text',
    dot: 'bg-stage-3-dot',
    bar: 'bg-stage-3-dot',
  },
  4: {
    tileBg: 'bg-stage-4-bg',
    border: 'border-stage-4-border',
    text: 'text-stage-4-text',
    dot: 'bg-stage-4-dot',
    bar: 'bg-stage-4-dot',
  },
};

/** Channel badge labels (grey neutral badges). */
export const KANAL_LABEL: Record<KontrolleKanal, string> = {
  email: 'E-Mail',
  postscan: 'Postscan',
  portal: 'Portal',
  versicherer_api: 'Versicherer-API',
};

/** Priorisierungssignal labels (salmon pills). */
export const SIGNAL_LABEL: Record<KontrolleSignalTyp, string> = {
  priorisierter_absender: 'Priorisierter Absender',
  regulatorische_frist: 'Regulatorische Frist',
  wiederholter_kontakt: 'Wiederholter Kontakt',
  werthaltig: 'Werthaltige Verknüpfung',
  schadenbezug: 'Schadenbezug',
  wiederkehrend: 'Wiederkehrend',
  erkannte_fristangabe: 'Erkannte Fristangabe',
  tonanalyse: 'Tonanalyse',
};

/** The zuweisen permission key — gates the assign action + the Frist field. */
export const KONTROLLE_ZUWEISEN_KEY = 'administration.kontrolle.rueckstand.zuweisen';

/** The einsehen permission key — gates the whole panel + the list + the detail. */
export const KONTROLLE_EINSEHEN_KEY = 'administration.kontrolle.rueckstand.einsehen';

/** Type guard for a stufe route param ("1".."4"). */
export function parseStufe(raw: string | undefined): KontrolleStufe | null {
  const n = Number(raw);
  return n === 1 || n === 2 || n === 3 || n === 4 ? (n as KontrolleStufe) : null;
}
